<template>
	<modal>
		<div
			class="bg-gradient-to-tr from-indigo-700/80 via-sky-600/80 to-fuchsia-500/80 backdrop-blur-sm rounded-lg shadow-lg sm:rounded-xl p-1 sm:p-2"
		>
			<div class="p-4 bg-zinc-50 text-zinc-900 rounded-md sm:rounded-lg">
				<template v-if="state.loading">
					<div class="py-16 text-center text-4xl font-poppins font-medium tracking-tight">
						Loading
						<i class="fa-spin far fa-spinner-third"></i>
					</div>
				</template>

				<template v-else-if="state.error">
					<div class="text-xl font-poppins font-medium tracking-tight">Snap Failed</div>

					<div class="my-12">We couldn't execute your snap right now. Try again later.</div>

					<div class="my-6 text-center">
						<div class="inline-flex gap-6">
							<button @click="$emit('close')">Close</button>
						</div>
					</div>
				</template>

				<template v-else-if="state.processing">
					<div class="py-6 text-center text-xl md:text-4xl font-poppins font-medium tracking-tight">
						🫰 Snap In Progress
					</div>

					<div class="max-w-36 mx-auto py-24" :style="{ perspective: '1000px' }">
						<div class="relative aspect-[5/7] transform-gpu preserve-3d animate-spin-card origin-center">
							<img
								:src="snap.asset.image"
								class="absolute inset-0 rounded-md shadow-md backface-hidden object-contain max-h-full"
							/>
							<img
								v-if="snap.next"
								:src="snap.next.asset.image"
								class="absolute inset-0 rounded-md shadow-md rotate-y-1/2 backface-hidden object-contain max-h-full"
							/>
							<img
								v-else-if="snap.prize_pool"
								src="/image/snap-pax/final-reveal-320x.webp"
								class="absolute inset-0 rounded-md shadow-md rotate-y-1/2 backface-hidden object-contain max-h-full"
							/>
						</div>
					</div>
				</template>

				<template v-else-if="snap">
					<div class="text-xl font-poppins font-medium tracking-tight flex items-center">
						<div>Snap {{ snap.asset.name }}</div>
						<div class="flex-grow"></div>
						<div
							class="text-sm uppercase px-6 py-2 rounded-full tracking-normal font-normal bg-gradient-to-tr from-purple-500 to-fuchsia-500 text-white whitespace-nowrap"
						>
							Round {{ snap.level }}
						</div>
					</div>

					<div class="grid grid-cols-2 gap-2 sm:gap-6 mx-auto max-w-96 mt-8 mb-4 justify-items-center">
						<div>
							<div class="font-poppins text-center mb-2">You Own</div>
							<img
								:src="$cdnify(snap.asset.image, 320)"
								:alt="snap.asset.name"
								class="rounded-md mx-auto"
							/>
							<div class="mt-2 text-center">
								<span class="text-fuchsia-500">{{ owned }}&times;</span> {{ snap.asset.name }}
							</div>
						</div>

						<div>
							<div class="font-poppins text-center mb-2" v-if="snap.modulus_threshold === snap.modulus">
								You Will Receive
							</div>
							<div class="font-poppins text-center mb-2" v-else>You Could Win*</div>

							<template v-if="snap.next">
								<div class="rounded-md mx-auto">
									<img
										:src="$cdnify(snap.next.asset.image, 320)"
										:alt="snap.next.asset.name"
										class="rounded-md"
									/>
								</div>
								<div class="mt-2 text-center">{{ snap.next.asset.name }}</div>
							</template>
							<template v-else>
								<div class="rounded-md mx-auto">
									<img
										src="/image/snap-pax/final-reveal-320x.webp"
										:alt="snap.prize_pool.name"
										class="rounded-md"
									/>
								</div>
								<div class="mt-2 text-center">{{ snap.prize_pool.name }}</div>
								<div class="mt-1 text-xs text-center empty:hidden">
									{{ snap.prize_pool.description }}
								</div>
							</template>

							<div
								class="mt-1 text-center text-xs text-zinc-500"
								v-if="snap.modulus_threshold < snap.modulus"
							>
								*Effective odds: {{ effectiveOdds }}
							</div>
						</div>
					</div>

					<div class="font-poppins mt-8 text-center">How many do you want to snap?</div>
					<div
						class="grid grid-cols-[3fr,3fr,4fr,3fr,3fr] bg-fuchsia-200 p-2 rounded-lg gap-2 items-center w-72 mx-auto text-center mt-4 mb-8 text-xl font-poppins"
					>
						<button
							@click="state.tokens = 1"
							:disabled="state.tokens === 1"
							class="bg-fuchsia-500/40 text-fuchsia-950/40 enabled:bg-fuchsia-500 enabled:text-fuchsia-950 rounded-md aspect-square text-sm uppercase"
						>
							1
						</button>
						<button
							@click="state.tokens--"
							:disabled="state.tokens <= 1"
							class="bg-fuchsia-500/40 text-fuchsia-950/40 enabled:bg-fuchsia-500 enabled:text-fuchsia-950 rounded-md aspect-square"
						>
							&lt;
						</button>
						<div class="tabular-nums">{{ state.tokens }}</div>
						<button
							@click="state.tokens++"
							:disabled="state.tokens >= Math.min(owned, MAX_SNAP_AMOUNT)"
							class="bg-fuchsia-500/40 text-fuchsia-950/40 enabled:bg-fuchsia-500 enabled:text-fuchsia-950 rounded-md aspect-square"
						>
							&gt;
						</button>
						<button
							@click="state.tokens = Math.min(owned, MAX_SNAP_AMOUNT)"
							:disabled="state.tokens === Math.min(owned, MAX_SNAP_AMOUNT)"
							class="bg-fuchsia-500/40 text-fuchsia-950/40 enabled:bg-fuchsia-500 enabled:text-fuchsia-950 rounded-md aspect-square text-sm uppercase"
						>
							Max
						</button>
					</div>

					<div class="my-6 text-center">
						<div class="inline-flex gap-6">
							<button @click="$emit('close')">Close</button>
							<button
								class="bg-zinc-300 text-zinc-500 enabled:bg-gradient-to-tr enabled:from-cyan-400 enabled:via-purple-500 enabled:to-fuchsia-500 enabled:text-white font-poppins font-medium block py-4 px-16 w-full max-w-64 mx-auto rounded-md hover:scale-105 hover:saturate-150 transition-all enabled:shadow-lg enabled:shadow-fuchsia-400/40"
								:disabled="state.processing"
								@click="handleStartSnap"
							>
								🫰 Snap
							</button>
						</div>
					</div>
				</template>
			</div>
		</div>
	</modal>
</template>

<script lang="ts" setup>
import { reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import { Snap, SnapPrize } from '@/types/Snap'
import { useSnapStore } from '@/stores/SnapStore'

import router from '@/router'
import { msleep } from '@/util/sleep'

const snapStore = useSnapStore()
const route = useRoute()

const props = defineProps<{
	owned: number
	snap: number | string
}>()

const MAX_SNAP_AMOUNT = 100

const state = reactive<{
	loading: boolean
	processing: boolean
	error: string | null

	tokens: number
	result: string | null
}>({
	loading: true,
	processing: false,
	error: null,

	tokens: Math.min(props.owned, 1),
	result: null,
})

onMounted(async () => {
	await snapStore.load(`${props.snap}`)
	state.loading = false
})

const snap = computed<Snap | null>((): Snap | null => {
	return snapStore.snaps[props.snap] ?? null
})

function now() {
	return +new Date()
}

async function handleStartSnap() {
	state.processing = true
	try {
		const res = await snapStore.execute(props.snap.toString(), state.tokens)
		state.result = res.snap_result_id

		const start = now()
		do {
			const res = await snapStore.loadResult(state.result)
			if (res.status === 'COMPLETE') {
				break
			}

			await msleep(1_000)
		} while (now() - start < 45_000)

		if (now() - start < 2_000) {
			await msleep(2000 - (now() - start))
		}

		router.push({
			name: 'snap-result',
			params: {
				slug: route.params.slug,
				assetId: route.params.assetId,
				snapId: snap.value.id,
				resultId: state.result,
			},
		})
		state.processing = false
	} catch (e) {
		state.error = 'Error'
		state.processing = false
	}
}

const effectiveOdds = computed<string>((): string => {
	if (snap.value.modulus <= 0 || snap.value.modulus_threshold <= 0) {
		return 'N/A'
	}

	const num = 1
	const den = snap.value.modulus / snap.value.modulus_threshold

	return `${num} in ${den}`
})
</script>
